import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from './cn.ts';

export const controlClass =
  'block min-w-0 w-full rounded-lg border border-slate-200 bg-white px-3 h-10 text-sm text-slate-900 placeholder:text-slate-400 transition-colors hover:border-slate-300 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:border-brand ' +
  'disabled:bg-slate-50 disabled:text-slate-500 aria-[invalid=true]:border-red-500';

/**
 * Champ de formulaire : libellé relié au contrôle, aide, et message d'erreur du serveur À CÔTÉ du champ.
 * `children` reçoit l'identifiant et les attributs ARIA à poser sur le contrôle.
 */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
  children: (props: { id: string; 'aria-invalid': boolean; 'aria-describedby'?: string }) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cn('min-w-0 space-y-1.5', className)}>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
        {required ? <span className="text-red-600" aria-hidden> *</span> : null}
      </label>
      {children({ id, 'aria-invalid': !!error, 'aria-describedby': describedBy })}
      {hint ? <p id={hintId} className="text-xs text-slate-500">{hint}</p> : null}
      {error ? <p id={errorId} className="text-xs font-medium text-red-700" data-testid="field-error">{error}</p> : null}
    </div>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(controlClass, className)} {...props} />;
}

export function Select({ className, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select className={cn(controlClass, 'pr-8', className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(controlClass, 'h-auto min-h-20 py-2', className)} {...props} />;
}

/** Case à cocher native (clavier et lecteurs d'écran sans surprise), aux couleurs de l'entreprise. */
export function Checkbox({ className, label, ...props }: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode }) {
  const box = (
    <input
      type="checkbox"
      className={cn(
        'h-4 w-4 shrink-0 cursor-pointer rounded border-slate-300 accent-[var(--color-brand)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
  if (!label) return box;
  return (
    <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
      {box}
      {label}
    </label>
  );
}
